/**
 * Integrations — connect the user's social accounts (OAuth) so they can publish
 * videos they make in Vivideo. Connecting happens HERE (not on the finished-video
 * screen). TikTok/Facebook/Instagram are live via the provider-agnostic v2-social
 * backend; other platforms render as "coming soon" until their app review lands.
 */
import { useEffect, useState } from 'react';
import { Loader2, X, Users, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/v2/toast';
import { vConfirm } from '@/v2/dialogs';
import { useUserStore } from '@/store';
import { useUpgradeModal } from '@/v2/upgradeModal';
import { BrandIcon, BRAND } from '@/v2/components/BrandIcons';
import { useIntegrationsAllowed, availableProvidersFor } from '@/v2/integrationsAccess';
import {
  startSocialConnect,
  disconnectSocial,
  fetchSocialAccounts,
  type SocialProvider,
  type SocialAccount,
} from '@/v2/social';

// `reqKey` (optional) = a pre-connect account requirement Meta enforces: Facebook
// only publishes to a Page, Instagram needs a Professional (Business/Creator)
// account. Shown on the card BEFORE the user connects so they set it up first.
const PLATFORMS: { provider: SocialProvider; descKey: string; reqKey?: string }[] = [
  { provider: 'tiktok', descKey: 'tiktokDesc' },
  { provider: 'youtube', descKey: 'youtubeDesc' },
  { provider: 'instagram', descKey: 'instagramDesc', reqKey: 'instagramReq' },
  { provider: 'threads', descKey: 'threadsDesc' },
  { provider: 'twitter', descKey: 'twitterDesc' },
  { provider: 'linkedin', descKey: 'linkedinDesc' },
  { provider: 'facebook', descKey: 'facebookDesc', reqKey: 'facebookReq' },
  { provider: 'googledrive', descKey: 'googledriveDesc' },
  // Coming soon (not in AVAILABLE_PROVIDERS → rendered as "Coming soon" cards):
  { provider: 'pinterest', descKey: 'pinterestDesc' },
  { provider: 'reddit', descKey: 'redditDesc' },
  { provider: 'vk', descKey: 'vkDesc' },
  { provider: 'dailymotion', descKey: 'dailymotionDesc' },
  { provider: 'discord', descKey: 'discordDesc' },
  { provider: 'bluesky', descKey: 'blueskyDesc' },
];

// Providers whose follower count is a REAL API read (FB pages_read_engagement,
// IG followers_count, threads_manage_insights). Always show the metric for these —
// even at 0 — it's honest UX and the visible use of those read permissions that
// Meta App Review looks for in screencasts.
const METRIC_PROVIDERS = new Set<SocialProvider>(['facebook', 'instagram', 'threads']);

const fmtFollowers = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  : String(n);

export function IntegrationsPage() {
  const { t } = useTranslation('v2');
  const allowed = useIntegrationsAllowed();
  const isPaid = useUserStore((s) => s.isPro());
  const showUpgrade = useUpgradeModal((s) => s.show);
  // Integrations are a PAID feature. Free (non-allowlist) users can still browse and
  // click "Connect" on ANY platform — but each Connect pushes them to upgrade instead
  // of starting OAuth. Allowlisted test/review accounts keep full access on any plan.
  const gated = !isPaid && !allowed;
  // Allowlist → all providers; public → PUBLIC_PROVIDERS (LinkedIn), rest = coming soon.
  // Gated (free) users see every platform as connectable so the whole grid funnels to upgrade.
  const availableSet = new Set<SocialProvider>(availableProvidersFor(allowed));
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<SocialProvider | null>(null);

  const load = async () => {
    try { setAccounts(await fetchSocialAccounts()); } catch { /* show all as unconnected */ }
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const connectedFor = (p: SocialProvider) => accounts.find((a) => a.provider === p);

  const handleConnect = async (p: SocialProvider, reqKey?: string) => {
    // Free users: any Connect click is a paywall — push to upgrade, don't start OAuth.
    if (gated) { showUpgrade({ kind: 'pro_feature' }); return; }
    // Platforms with an account requirement (Facebook Page, Instagram Professional)
    // show it in a window first; the user acknowledges, then OAuth starts.
    if (reqKey && !(await vConfirm(t(`integrations.${reqKey}`), {
      title: t('integrations.beforeConnectTitle', { name: BRAND[p].label }),
      confirmLabel: t('integrations.connect'),
    }))) return;
    setBusy(p);
    void startSocialConnect(p, window.location.pathname).catch(() => {
      toast.error(t('integrations.connectError', { name: BRAND[p].label }));
      setBusy(null);
    });
  };

  const handleDisconnect = async (p: SocialProvider, account?: SocialAccount) => {
    const who = account?.username ? `@${account.username}` : account?.display_name || BRAND[p].label;
    if (!(await vConfirm(t('integrations.disconnectConfirm', { name: who }), {
      title: t('integrations.disconnectTitle', { name: BRAND[p].label }),
      confirmLabel: t('integrations.disconnect'),
      danger: true,
    }))) return;
    setBusy(p);
    try { await disconnectSocial(p); await load(); }
    catch { toast.error(t('integrations.disconnectError')); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-text-primary mb-2">{t('integrations.title')}</h1>
      <p className="text-text-secondary mb-8">{t('integrations.subtitle')}</p>

      {/* Free users: a Pro upsell banner asking them to upgrade to connect accounts. */}
      {gated && (
        <div className="glass rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 border border-primary/30">
          <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0 shadow-glow">
            <Crown className="w-5 h-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-text-primary">{t('integrations.upgradeBannerTitle')}</h2>
            <p className="text-sm text-text-secondary">{t('integrations.upgradeBannerBody')}</p>
          </div>
          <button
            onClick={() => showUpgrade({ kind: 'pro_feature' })}
            className="shrink-0 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-95 hover:shadow-glow transition-all"
          >
            {t('integrations.upgradeBannerCta')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {PLATFORMS.map(({ provider, descKey, reqKey }) => {
          // Free users can click Connect on every platform (all funnel to upgrade).
          const available = gated ? true : availableSet.has(provider);
          const account = available ? connectedFor(provider) : undefined;
          const isConnected = !!account;
          const isBusy = busy === provider;
          const brand = BRAND[provider];
          const handle = account?.username ? `@${account.username}` : account?.display_name;

          return (
            <div key={provider} className="glass rounded-2xl p-6 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: brand.color }}>
                  <BrandIcon provider={provider} className="w-6 h-6 text-white" />
                </div>
                {/* Connected → the account's @handle + a green "live" dot, right of the icon. */}
                {isConnected && handle && (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-semibold text-text-primary truncate">{handle}</span>
                    <span className="w-2 h-2 rounded-full bg-green-500 shrink-0 animate-pulse" title={t('integrations.connected')} />
                  </span>
                )}
              </div>

              <div className="flex-1">
                <h3 className="font-bold text-text-primary mb-1">{brand.label}</h3>
                {isConnected ? (
                  (account?.follower_count || METRIC_PROVIDERS.has(provider)) ? (
                    <p className="text-[11px] text-text-tertiary flex items-center gap-1">
                      <Users className="w-3 h-3" /> {t('integrations.followers', { n: fmtFollowers(account?.follower_count || 0) })}
                    </p>
                  ) : null
                ) : (
                  <p className="text-sm text-text-tertiary">{t(`integrations.${descKey}`)}</p>
                )}
              </div>

              {!available ? (
                <div className="w-full py-2.5 rounded-full bg-surface-light/40 text-sm font-semibold text-text-tertiary flex items-center justify-center">
                  {t('integrations.comingSoon')}
                </div>
              ) : isConnected ? (
                <button
                  onClick={() => void handleDisconnect(provider, account)}
                  disabled={isBusy}
                  className="w-full py-2.5 rounded-full glass text-sm font-semibold text-text-secondary hover:text-error transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  {t('integrations.disconnect')}
                </button>
              ) : (
                <button
                  onClick={() => void handleConnect(provider, reqKey)}
                  disabled={isBusy || loading}
                  className="w-full py-2.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-95 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('integrations.connect')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

